'use client';

import CheckIcon from '@mui/icons-material/Check';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import MemoryIcon from '@mui/icons-material/Memory';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import { Box } from '@mui/material';
import { alpha, type SxProps, type Theme, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';
import { useI18n } from '../../hooks';

export type AstorReasoningStepStatus = 'success' | 'pending' | 'error';

export interface AstorReasoningStep {
  /** Stable id (typically the tool-call id from the AI run). */
  id: string;
  /** Tool invocation as a mono string (e.g. `policy.evaluate(riverstone_bridge_iv)`). */
  tool: string;
  /** Outcome summary (e.g. `5/7 gates pass`, `LTC 83.3% · LTARV 61.4%`). */
  result?: ReactNode;
  /** Duration label (e.g. `124ms`). Optional; omitted for steps without timing. */
  ms?: string;
  /** Visual outcome — success/pending/error. */
  status?: AstorReasoningStepStatus;
}

export interface AstorReasoningTraceProps {
  /** Ordered list of tool steps. Render order is preserved. */
  steps: AstorReasoningStep[];
  /**
   * Total elapsed time across all steps (e.g. `1.4s`). Rendered as the
   * caption next to the "REASONING TRACE" header. Omit if not available.
   */
  totalDuration?: string;
  /** Optional override for the header label (defaults to i18n key). */
  headerLabel?: ReactNode;
  /**
   * Compact mode collapses padding and shrinks the icon column. Use inside
   * an existing reasoning surface (e.g. the AstorRecommendationCard body).
   */
  dense?: boolean;
  sx?: SxProps<Theme>;
}

/**
 * AstorReasoningTrace — boxed list of tool invocations.
 *
 * Used inside an active Astor chat response and as a sibling of the
 * recommendation prose. Each row reads as:
 *
 *   ✓ policy.evaluate(riverstone_bridge_iv) → 5/7 gates pass · 124ms
 *
 * The check icon uses mint for success, amber for pending, red for error.
 * Tool name and ms are monospace; the arrow `→` and result are sans, so
 * the eye lands on the tool list first and the outcomes second.
 *
 * Embed inside `AstorRecommendationCard`, the case-Overview reasoning panel,
 * or any chat surface that wants to expose tool-call traceability.
 */
export function AstorReasoningTrace({
  steps,
  totalDuration,
  headerLabel,
  dense = false,
  sx,
}: AstorReasoningTraceProps) {
  const theme = useTheme();
  const { t } = useI18n();
  const isDark = theme.palette.mode === 'dark';
  const mint = theme.palette.success.main;
  const amber = theme.palette.warning.main;
  const red = theme.palette.error.main;

  const statusIcon = (status: AstorReasoningStepStatus) => {
    if (status === 'pending') return <HourglassEmptyIcon sx={{ fontSize: 12, color: amber }} />;
    if (status === 'error') return <ReportProblemOutlinedIcon sx={{ fontSize: 12, color: red }} />;
    return <CheckIcon sx={{ fontSize: 12, color: mint }} />;
  };

  const statusColor = (status: AstorReasoningStepStatus) => {
    if (status === 'pending') return amber;
    if (status === 'error') return red;
    return mint;
  };

  return (
    <Box
      sx={[
        {
          p: dense ? 1 : 1.5,
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'divider',
          backgroundColor: alpha(theme.palette.background.paper, isDark ? 0.4 : 0.6),
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: dense ? 0.75 : 1 }}>
        <MemoryIcon sx={{ fontSize: 13, color: mint }} />
        <Typography
          variant="overline"
          sx={{
            fontFamily: 'ui-monospace, "SF Mono", Menlo, Monaco, Consolas, monospace',
            fontWeight: 600,
            color: 'text.secondary',
            letterSpacing: '0.08em',
            lineHeight: 1,
          }}
        >
          {headerLabel ?? t('astor.reasoningTrace.header')}
        </Typography>
        {totalDuration && (
          <Typography
            variant="caption"
            sx={{
              fontFamily: 'ui-monospace, "SF Mono", Menlo, Monaco, Consolas, monospace',
              color: 'text.disabled',
              lineHeight: 1,
            }}
          >
            · {totalDuration}
          </Typography>
        )}
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: dense ? 0.25 : 0.5 }}>
        {steps.map((s) => {
          const status: AstorReasoningStepStatus = s.status ?? 'success';
          const accent = statusColor(status);
          return (
            <Box
              key={s.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                fontFamily: 'ui-monospace, "SF Mono", Menlo, Monaco, Consolas, monospace',
                fontSize: '0.75rem',
                py: 0.25,
              }}
            >
              {statusIcon(status)}
              <Typography
                component="span"
                sx={{
                  fontFamily: 'inherit',
                  fontSize: '0.75rem',
                  color: accent,
                  flexShrink: 0,
                  fontWeight: 500,
                }}
              >
                {s.tool}
              </Typography>
              {s.result !== undefined && (
                <>
                  <Typography component="span" sx={{ color: 'text.disabled', fontSize: '0.75rem' }}>
                    →
                  </Typography>
                  <Typography
                    component="span"
                    sx={{
                      flex: 1,
                      fontFamily: 'inherit',
                      fontSize: '0.75rem',
                      color: 'text.secondary',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {s.result}
                  </Typography>
                </>
              )}
              {s.ms && (
                <Typography
                  component="span"
                  sx={{
                    fontFamily: 'inherit',
                    fontSize: '0.6875rem',
                    color: 'text.disabled',
                    flexShrink: 0,
                  }}
                >
                  {s.ms}
                </Typography>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

AstorReasoningTrace.displayName = 'AstorReasoningTrace';
